import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const RADIUS_KEY = "curbside.default_radius";

export default function Settings() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [cur, setCur] = useState(""); const [nw, setNw] = useState(""); const [nw2, setNw2] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [prefs, setPrefs] = useState({
    msg_alerts: true, badge_polling: true, hide_exact_address: false, share_email_with_claimer: false,
  });
  const [theme, setTheme] = useState(localStorage.getItem("curbside.theme") || "system");
  const [radius, setRadius] = useState(Number(localStorage.getItem(RADIUS_KEY) || 10));

  useEffect(() => {
    if (!user) return;
    setName(user.name || "");
    if (user.preferences) setPrefs((p) => ({ ...p, ...user.preferences }));
  }, [user]);

  const saveName = async () => {
    setSavingName(true);
    try { await api.patch("/auth/me", { name }); toast.success("Name updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingName(false); }
  };

  const savePrefs = async (next) => {
    setPrefs(next);
    try { await api.patch("/auth/me", { preferences: next }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (nw !== nw2) { toast.error("New passwords don't match"); return; }
    setSavingPw(true);
    try { await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password changed"); setCur(""); setNw(""); setNw2(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingPw(false); }
  };

  const applyTheme = (t) => {
    setTheme(t);
    localStorage.setItem("curbside.theme", t);
    const root = document.documentElement;
    const isDark = t === "dark" || (t === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
    root.classList.toggle("dark", isDark);
  };

  const saveRadius = (r) => { setRadius(r); localStorage.setItem(RADIUS_KEY, String(r)); };

  const deleteAccount = async () => {
    if (!window.confirm("Permanently delete your account, posts and messages? This can't be undone.")) return;
    try {
      await api.delete("/auth/me");
      toast.success("Account deleted");
      await logout();
      nav("/");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const Row = ({ title, desc, checked, onChange, testid }) => (
    <div className="flex items-start justify-between gap-4 py-3">
      <div><div className="font-medium text-[#0F0F12]">{title}</div>
        <div className="text-sm text-[#6B7280]">{desc}</div></div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );

  return (
    <div className="min-h-[calc(100vh-4rem)] max-w-3xl mx-auto px-5 sm:px-8 py-10">
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-medium">Settings</h1>
      <p className="text-[#6B7280] mt-2">Tune your account, privacy and the app's behaviour.</p>

      <Tabs defaultValue="profile" className="mt-8">
        <TabsList className="bg-white border border-[#E8E5F0] rounded-full p-1 flex-wrap h-auto">
          <TabsTrigger value="profile" className="rounded-full" data-testid="tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="notifications" className="rounded-full" data-testid="tab-notifs">Notifications</TabsTrigger>
          <TabsTrigger value="privacy" className="rounded-full" data-testid="tab-privacy">Privacy</TabsTrigger>
          <TabsTrigger value="appearance" className="rounded-full" data-testid="tab-appearance">Appearance</TabsTrigger>
          <TabsTrigger value="account" className="rounded-full" data-testid="tab-account">Account</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-6 bg-white border border-[#E8E5F0] rounded-2xl p-6 space-y-5">
          <div>
            <Label>Display name</Label>
            <div className="mt-1.5 flex gap-2">
              <Input value={name} onChange={(e) => setName(e.target.value)} className="h-11 rounded-xl" data-testid="settings-name" />
              <Button onClick={saveName} disabled={savingName || !name.trim()}
                className="h-11 rounded-full bg-[#7C3AED] hover:bg-[#5B21B6] text-white px-5" data-testid="save-name">Save</Button>
            </div>
          </div>
          <div>
            <Label>Email</Label>
            <Input value={user?.email || ""} disabled className="mt-1.5 h-11 rounded-xl bg-[#F4F0FB]" />
          </div>
          <form onSubmit={changePw} className="border-t border-[#E8E5F0] pt-5 space-y-3">
            <div className="font-medium">Change password</div>
            <Input type="password" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} className="h-11 rounded-xl" data-testid="pw-current" />
            <Input type="password" placeholder="New password" value={nw} onChange={(e) => setNw(e.target.value)} className="h-11 rounded-xl" data-testid="pw-new" />
            <Input type="password" placeholder="Confirm new password" value={nw2} onChange={(e) => setNw2(e.target.value)} className="h-11 rounded-xl" data-testid="pw-confirm" />
            <Button type="submit" disabled={savingPw || !cur || !nw}
              className="h-11 rounded-full bg-[#0F0F12] hover:bg-black text-white px-5" data-testid="save-pw">Update password</Button>
          </form>
        </TabsContent>

        <TabsContent value="notifications" className="mt-6 bg-white border border-[#E8E5F0] rounded-2xl p-6 divide-y divide-[#E8E5F0]">
          <Row title="Message alerts" desc="Show a toast when a new message arrives." checked={prefs.msg_alerts} onChange={(v) => savePrefs({ ...prefs, msg_alerts: v })} testid="pref-msg-alerts" />
          <Row title="Header badge auto-refresh" desc="Poll for new messages every 10 seconds." checked={prefs.badge_polling} onChange={(v) => savePrefs({ ...prefs, badge_polling: v })} testid="pref-badge-polling" />
        </TabsContent>

        <TabsContent value="privacy" className="mt-6 bg-white border border-[#E8E5F0] rounded-2xl p-6 divide-y divide-[#E8E5F0]">
          <Row title="Hide exact address on new posts" desc="Only show the cross-street/area, not the full address." checked={prefs.hide_exact_address} onChange={(v) => savePrefs({ ...prefs, hide_exact_address: v })} testid="pref-hide-address" />
          <Row title="Share my email with claimers" desc="Let neighbours who claim your items see your email." checked={prefs.share_email_with_claimer} onChange={(v) => savePrefs({ ...prefs, share_email_with_claimer: v })} testid="pref-share-email" />
          <div className="py-3">
            <Label>Default search radius (km)</Label>
            <select value={radius} onChange={(e) => saveRadius(Number(e.target.value))}
              className="mt-1.5 h-11 w-full rounded-xl border border-input bg-white px-3 text-sm" data-testid="pref-radius">
              {[2, 5, 10, 25, 50].map((r) => <option key={r} value={r}>{r} km</option>)}
            </select>
          </div>
        </TabsContent>

        <TabsContent value="appearance" className="mt-6 bg-white border border-[#E8E5F0] rounded-2xl p-6">
          <Label>Theme</Label>
          <div className="mt-2 inline-flex bg-[#F4F0FB] rounded-full p-1">
            {["light", "dark", "system"].map((t) => (
              <button key={t} onClick={() => applyTheme(t)} data-testid={`theme-${t}`}
                className={`px-4 h-9 rounded-full text-sm capitalize ${theme === t ? "bg-[#7C3AED] text-white" : "text-[#0F0F12]"}`}>
                {t}
              </button>
            ))}
          </div>
          <p className="text-xs text-[#6B7280] mt-2">Dark mode is a preview — some pages still use the light palette.</p>
        </TabsContent>

        <TabsContent value="account" className="mt-6 bg-white border border-red-100 rounded-2xl p-6">
          <div className="font-medium text-red-600">Danger zone</div>
          <p className="text-sm text-[#6B7280] mt-2 mb-4">Deleting your account removes all your posts and messages permanently.</p>
          <Button onClick={deleteAccount} variant="outline"
            className="h-11 rounded-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
            data-testid="delete-account">Delete my account</Button>
        </TabsContent>
      </Tabs>
    </div>
  );
}
